/**
 * What is payable besides the price, per offer.
 *
 * **The offer is the whole point.** A unit can be to let *and* for sale at once — one of the
 * commonest signs on a building here — and the two carry completely different charges. A caution
 * deposit and a service charge belong to the tenancy; a commission of five per cent of ₦180m
 * belongs to the sale. One list for both would put a deposit on the sale advert and a commission
 * on the tenancy, quoted against a price nobody is paying.
 *
 * So there is one block per priced offer, and each block only offers the charges that offer can
 * carry. A joint venture gets no block at all: it has no price, so there is nothing for a
 * percentage to be a percentage of.
 *
 * **Flat or percentage, per charge, because that is how they are quoted.** A deposit is a figure;
 * an agency fee is "ten per cent". Storing the rate rather than the figure it works out to is what
 * makes the advert stay right when the asking price moves.
 *
 * The running total under each block is the point of the whole screen: it is the number somebody
 * is actually going to be asked for, and nobody should have to work it out on paper to check that
 * what they have typed is what they meant.
 */

import { useMemo, useState } from "react";
import {
  CHARGES_ALLOWED,
  CHARGE_LABEL_MAX,
  OFFER_TYPES,
  chargeLabel,
  formatMoney,
  unitChargeAmount,
  type ChargeType,
  type OfferType,
  type UnitCharge,
} from "@rentorng/core";
import { Button, Field, Input, Select } from "./ui";
import { MoneyInput } from "./money-input";
import { IconPlus, IconTrash } from "./icons";
import { BeneficiaryPicker } from "./beneficiary-picker";
import { useSession } from "@/lib/session";

interface Props {
  offers: string[];
  /** What each offer is being asked for, in minor units. A percentage resolves against it. */
  prices: Partial<Record<OfferType, number | null>>;
  value: UnitCharge[];
  onChange: (next: UnitCharge[]) => void;
}

/*
  A key that belongs to the row, not to where the row happens to sit.

  React reuses a rendered node when its key matches, so keying by array index tells it that the
  third row is still the third row after the second one is removed — and it hands the third row's
  controls to what is now the second charge. Every control in here is driven from `value`, so today
  that shows as a redraw rather than as a wrong figure; the moment one of them holds anything of its
  own — a half-typed amount, a caret position, the selected text in the name box — it becomes a
  value appearing on the neighbouring charge, which is the hardest sort of fault to be believed
  about because the stored JSON is right and only the screen is wrong.

  The key cannot live on the charge itself. `UnitCharge` is the shape written to the database and
  read back by the public price breakdown, so a key put there would be stored, sent to strangers and
  compared in every round-trip check. It is held beside the list instead — one entry per row, moved
  and dropped in step with it — which is why every mutation below writes `keys` as well as calling
  `onChange`.
*/
let issued = 0;
const nextKey = () => `charge-${(issued += 1)}`;

export function UnitCharges({ offers, prices, value, onChange }: Props) {
  const { org } = useSession();
  const currency = String(org?.currency ?? "NGN");

  /*
    Reconciled during the render rather than in an effect, because a key that arrives one paint late
    is a key that was wrong for the paint the person actually saw. Every change made through this
    editor writes both lists together, so a length that does not match means the parent replaced the
    charges wholesale — a saved unit finishing loading, or the form being reset — and the keys have
    to be made to fit the list that arrived.
  */
  const [keys, setKeys] = useState<string[]>(() => value.map(() => nextKey()));
  const rowKeys = keys.length === value.length ? keys : value.map((_, index) => keys[index] ?? nextKey());
  if (rowKeys !== keys) setKeys(rowKeys);

  const shown = useMemo(
    () => OFFER_TYPES.filter((offer) => offers.includes(offer.value) && CHARGES_ALLOWED[offer.value].length > 0),
    [offers],
  );

  if (!shown.length) {
    return (
      <p className="text-sm text-ink-500">
        Nothing to add here — a joint venture is negotiated, so there is no price for a charge to sit on top of.
      </p>
    );
  }

  const replace = (index: number, patch: Partial<UnitCharge>) =>
    onChange(value.map((charge, i) => (i === index ? { ...charge, ...patch } : charge)));

  /*
    Adding and removing move the keys alongside the charges, which is the whole point of holding
    them: drop the row's key with the row and the two rows either side keep the nodes they already
    had, so nothing they were holding lands on a charge it does not belong to.

    A new row is `other`, always. It used to be the first type this offer did not already carry, so
    on a unit to let the rows arrived as a service charge, then a caution deposit, then an agency
    fee — and it changed its mind again whenever one of them was corrected, because the set of
    "already used" types had moved underneath it. The intent was to save a press; the effect was a
    control that appeared to be choosing on the operator's behalf, in an order nobody could see the
    rule behind — reported as "it fills its own in randomly". `other` is
    the one type that claims nothing, so the row says plainly that it is waiting to be told, and its
    name box is there to be filled the moment somebody means to leave it as "Other". This is what
    the tenancy form has always done, and the two disagreeing was half the fault.
  */
  const add = (offer: OfferType) => {
    onChange([...value, { offer, type: "other", basis: "fixed", value: 0 }]);
    setKeys([...rowKeys, nextKey()]);
  };

  const remove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
    setKeys(rowKeys.filter((_, i) => i !== index));
  };

  /*
    A name typed into the box, or — when the box is emptied — no name at all.

    The key is removed rather than left as an empty string, so a charge nobody named is stored as
    the same JSON it was stored as before this field existed. `normaliseCharges` applies the same
    rule again on the way out of the form, but it applies it to what is *sent*: leaving `label: ""`
    sitting in the editor's own state would mean the thing being read back and compared here is not
    the thing that will be written, and that difference is the sort that shows up months later in a
    round-trip check nobody can explain.
  */
  const rename = (index: number, typed: string) =>
    onChange(
      value.map((charge, i) => {
        if (i !== index) return charge;
        if (typed) return { ...charge, label: typed };
        const { label: _dropped, ...rest } = charge;
        return rest;
      }),
    );

  /*
    Who this charge's money is for, or — when nobody is chosen — no key at all.

    The same rule the name follows, and for the same reason: `UnitCharge` is the shape written to
    `units.charges` and read back by the public price breakdown, so a charge nobody has assigned has
    to be stored byte for byte as it was stored before this field existed. `normaliseCharges` drops
    anything that is not a string on the way in, so a `null` left here would survive the round trip
    only until the next save and then vanish — a difference that shows up months later in a check
    nobody can explain.

    It is never sent to a stranger. The field exists for the other end entirely: a unit's asking
    charges seed a tenancy's, so without it the answer to "who draws the agreement on this building"
    was thrown away every time a manager picked the unit. See ADR-0137.
  */
  const reassign = (index: number, beneficiaryId: string | null) =>
    onChange(
      value.map((charge, i) => {
        if (i !== index) return charge;
        if (beneficiaryId) return { ...charge, beneficiaryId };
        const { beneficiaryId: _dropped, ...rest } = charge;
        return rest;
      }),
    );

  return (
    <div className="space-y-5">
      {shown.map((offer) => {
        const price = prices[offer.value] ?? null;
        /*
          The rows in the order somebody made them, and **not** through `chargesFor`.

          `chargesFor` sorts by `CHARGES_ALLOWED`, which is right for every reader of these charges
          — the advert, the price breakdown, the offer letter all want a service charge before a
          legal fee whatever order they happened to be typed in — and wrong for the one place they
          are typed. Sorting an editor's rows on the value being edited means the row jumps out from
          under the hand that is editing it. `other` sorts last, so choosing any real type on the
          third of three rows moved that row to the top: the row somebody had just touched went on
          reading "Other", and a row they had never touched now read "Service charge".

          That is the operator's sentence exactly — *"regardless of what type of charge I select, it
          fills its own in"* — and it was measured in a browser before this was changed: third row
          to "Service charge" landed on the first, third row to "Caution deposit" landed on the
          second. The tenancy form never had it, because it renders its rows in the order they were
          made. See ADR-0127.

          Nothing downstream is affected. The order written to `units.charges` is unchanged, and
          every reader calls `chargesFor` for itself, so the canonical order survives everywhere it
          was actually wanted. The total below sums these rows and a sum does not care about order.
        */
        const mine = value.filter((charge) => charge.offer === offer.value);
        const resolved = mine.map((charge) => unitChargeAmount(charge, price));
        const total = resolved.every((amount) => amount !== null)
          ? resolved.reduce((sum: number, amount) => sum + (amount ?? 0), 0)
          : null;

        return (
          <div key={offer.value} className="rounded-[var(--radius-surface)] border border-ink-200 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-ink-900">On top of the {offer.short.toLowerCase()}</p>
              {total ? (
                <p className="tabular text-sm font-semibold text-brand-700">{formatMoney(total, currency)}</p>
              ) : null}
            </div>

            {mine.length ? (
              <div className="space-y-3">
                {mine.map((charge) => {
                  const index = value.indexOf(charge);
                  const amount = unitChargeAmount(charge, price);
                  return (
                    <div key={rowKeys[index]} className="space-y-2">
                      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_9rem_minmax(0,11rem)_auto] sm:items-end">
                        <Field label="Charge" htmlFor={`charge-type-${index}`}>
                          <Select
                            id={`charge-type-${index}`}
                            value={charge.type}
                            onChange={(event) => replace(index, { type: event.target.value as ChargeType })}
                          >
                            {CHARGES_ALLOWED[offer.value].map((type) => (
                              <option key={type} value={type}>
                                {chargeLabel(type)}
                              </option>
                            ))}
                          </Select>
                        </Field>

                        <Field label="Quoted as" htmlFor={`charge-basis-${index}`}>
                          <Select
                            id={`charge-basis-${index}`}
                            value={charge.basis}
                            onChange={(event) =>
                              /*
                                The number means something different on each side — minor units one
                                way, basis points the other — so it is cleared rather than carried
                                across. 250,000 silently becoming 2,500% is worse than retyping it.
                              */
                              replace(index, { basis: event.target.value as UnitCharge["basis"], value: 0 })
                            }
                          >
                            <option value="fixed">A flat fee</option>
                            <option value="percent">A percentage</option>
                          </Select>
                        </Field>

                        <Field
                          label={charge.basis === "percent" ? "Per cent" : "Amount"}
                          htmlFor={`charge-value-${index}`}
                          hint={
                            charge.basis === "percent" && amount !== null
                              ? `= ${formatMoney(amount, currency)}`
                              : charge.basis === "percent"
                                ? "Of this offer's price"
                                : undefined
                          }
                        >
                          {charge.basis === "percent" ? (
                            <div className="relative">
                              <input
                                id={`charge-value-${index}`}
                                className="field pr-8"
                                type="text"
                                inputMode="decimal"
                                autoComplete="off"
                                value={charge.value ? String(charge.value / 100) : ""}
                                placeholder="10"
                                onChange={(event) => {
                                  const typed = Number(event.target.value.replace(/[^0-9.]/g, ""));
                                  replace(index, { value: Number.isFinite(typed) ? Math.round(typed * 100) : 0 });
                                }}
                              />
                              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-ink-400">
                                %
                              </span>
                            </div>
                          ) : (
                            <MoneyInput
                              symbol
                              id={`charge-value-${index}`}
                              value={charge.value ? String(charge.value / 100) : ""}
                              onChange={(canonical) => replace(index, { value: Math.round(Number(canonical || 0) * 100) })}
                            />
                          )}
                        </Field>

                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Remove ${chargeLabel(charge.type, charge.label)}`}
                          className="mb-1 shrink-0"
                          onClick={() => remove(index)}
                        >
                          <IconTrash size={16} />
                        </Button>
                      </div>

                      {/*
                        "Other" is the one type that does not say what it is, so it — and only it —
                        is given a line to say. The placeholder is the word the charge would carry
                        anyway, which is what makes leaving it empty a real choice rather than an
                        omission: the advert reads "Other" either way, exactly as it did before this
                        field existed. Sixty characters because the name ends up in a table cell, on
                        an invoice line and in a price breakdown on a phone.

                        The name is left alone when the type changes rather than wiped on the spot —
                        somebody who nudges the select and puts it straight back has not asked to
                        lose what they typed — and `normaliseCharges` drops it on the way out, which
                        is the one place the rule has to hold. The mark beside it follows the type
                        immediately, because `chargeLabel` ignores a label on anything but `other`.
                      */}
                      {charge.type === "other" ? (
                        <Field label="Called" htmlFor={`charge-label-${index}`}>
                          <Input
                            id={`charge-label-${index}`}
                            value={charge.label ?? ""}
                            maxLength={CHARGE_LABEL_MAX}
                            placeholder="Other"
                            /* The same handle the tenancy form's box carries, so "the box a charge
                               is named in" is one thing to look for wherever it is drawn. */
                            aria-label="What this charge is called"
                            onChange={(event) => rename(index, event.target.value)}
                          />
                        </Field>
                      ) : null}

                      {/*
                        Whose money it is, answered once on the building rather than on every
                        tenancy drawn against it.

                        Left as "not yet decided" the tenancy form falls to the server's own
                        default — rent and the caution deposit to the owner of the unit, the
                        agency's own fees to the agency — which is exactly what happened before
                        this control existed. The one it cannot guess is the legal fee, and that is
                        the one this box is really here for.
                      */}
                      <Field
                        label="For"
                        htmlFor={`charge-beneficiary-${index}`}
                        hint="Carried across to any tenancy drawn against this unit, where it can still be changed."
                      >
                        <BeneficiaryPicker
                          id={`charge-beneficiary-${index}`}
                          ariaLabel="Who this charge is for"
                          value={charge.beneficiaryId ?? ""}
                          onChange={(next) => reassign(index, next)}
                        />
                      </Field>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-ink-500">Nothing on top of this one.</p>
            )}

            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => add(offer.value)}
            >
              <IconPlus size={15} />
              Add a charge
            </Button>
          </div>
        );
      })}
    </div>
  );
}
